import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  ButtonGroup,
  Stack,
} from "@mui/material";
import { Refresh as RefreshIcon, Search } from "@mui/icons-material";
import Loader from "@jumbo/components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { usePagination } from "app/hooks/usePagination";
import { Table as CustomTable } from "app/components";
import moment from "moment";
import { useSnackbar } from "notistack";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { getMeeshoProductReportList } from "app/redux/slice/meeshoOrderManagementSlice";

const DEFAULT_SORT_FIELD = "netPL";
const DEFAULT_SORT_ORDER = "asc";
const DEFAULT_GROUP_BY = "size";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const buildParams = ({ page, rowsPerPage, order, orderBy, dateRange, searchTerm, groupBy }) => {
  const params = {
    page,
    perPage: rowsPerPage,
    countOn: "CostPrice",
    groupBy,
  };
  if (orderBy) {
    params.sort = order === "asc" ? 1 : -1;
    params.field = orderBy;
  }
  if (searchTerm) params.search = searchTerm.trim();
  if (dateRange[0] && dateRange[1]) {
    params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
    params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
  }
  return params;
};

const ProductReportList = () => {
  const dispatch = useDispatch();
  const meeshoData = useSelector((state) => state?.meeshoOrderManagement);
  const orders = meeshoData?.productReport?.list || [];
  const { enqueueSnackbar } = useSnackbar();

  const [dateRange, setDateRange] = useState([null, null]);
  const [tempSearchTerm, setTempSearchTerm] = useState("");

  // Applied state (committed on Apply)
  const [appliedDateRange, setAppliedDateRange] = useState([null, null]);
  const [appliedSearch, setAppliedSearch] = useState("");

  const [groupBy, setGroupBy] = useState(DEFAULT_GROUP_BY);
  const [tableResetKey, setTableResetKey] = useState(0);
  const [order, setOrder] = useState(DEFAULT_SORT_ORDER);
  const [orderBy, setOrderBy] = useState(DEFAULT_SORT_FIELD);

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "meesho-management-product-report" });

  useEffect(() => {
    dispatch(
      getMeeshoProductReportList(
        buildParams({
          page,
          rowsPerPage,
          order,
          orderBy,
          dateRange: appliedDateRange,
          searchTerm: appliedSearch,
          groupBy,
        })
      )
    );
  }, [dispatch, page, rowsPerPage, order, orderBy, appliedDateRange, appliedSearch, groupBy]);

  const handleApplyFilter = () => {
    if ((dateRange[0] && !dateRange[1]) || (!dateRange[0] && dateRange[1])) {
      enqueueSnackbar("Please select both start and end date", {
        variant: "error",
      });
      return;
    }
    if (page !== 1) handleChangePage(null, 0);
    setAppliedDateRange(dateRange);
    setAppliedSearch(tempSearchTerm);
  };

  const handleClear = () => {
    setTempSearchTerm("");
    setDateRange([null, null]);
    setAppliedDateRange([null, null]);
    setAppliedSearch("");
    setGroupBy(DEFAULT_GROUP_BY);
    setOrder(DEFAULT_SORT_ORDER);
    setOrderBy(DEFAULT_SORT_FIELD);
    setTableResetKey((k) => k + 1);
    if (page !== 1) handleChangePage(null, 0);
  };

  const switchGroupBy = (newGroupBy) => {
    setGroupBy(newGroupBy);
    setOrder(DEFAULT_SORT_ORDER);
    setOrderBy(DEFAULT_SORT_FIELD);
    setTableResetKey((k) => k + 1);
    if (page !== 1) handleChangePage(null, 0);
  };

  // ── Column definitions ─────────────────────────────────────────────────────

  const sizeColumns = [
    { id: "itemName", label: "Item Name", sort: true },
    { id: "sku", label: "SKU", sort: true },
    {
      id: "perUnitCost",
      label: "Cost Per Unit",
      sort: true,
      isTotal: true,
      format: (v) => Number(v || 0).toFixed(2),
    },
    {
      id: "totalOrdersCount",
      label: "Total Orders",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "totalDeliveredOrdersCount",
      label: "Total Delivered",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "totalReturnOrdersCount",
      label: "Total Returns",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "returnPercentage",
      label: "Return %",
      sort: true,
      render: (row) => <span>{Number(row?.returnPercentage || 0).toFixed(1)}%</span>,
    },
    {
      id: "totalCost",
      label: "Total Cost (A)",
      sort: true,
      isTotal: true,
      format: (v) => Number(v || 0).toFixed(2),
    },
    {
      id: "totalReceivedPayment",
      label: "Received Payment (B)",
      sort: true,
      isTotal: true,
      format: (v) => Number(v || 0).toFixed(2),
    },
    {
      id: "totalReturnPayment",
      label: "Return Charges (C)",
      sort: true,
      render: (row) => Math.abs(Number(row.totalReturnPayment || 0)).toFixed(2),
      isTotal: true,
      format: (v) => Number(v || 0).toFixed(2),
    },
    {
      id: "netPL",
      label: "Gross P/L (B-A-C)",
      sort: true,
      minWidth: 130,
      render: (row) => {
        const value = Number(row?.netPL || 0);
        return (
          <span style={{ color: value >= 0 ? "green" : "red", fontWeight: "bold" }}>
            {value >= 0 ? "+" : "-"}
            {Math.abs(value).toFixed(2)}
          </span>
        );
      },
      isTotal: true,
      format: (v) => Number(v || 0).toFixed(2),
    },
  ];

  const colorColumns = [
    {
      id: "productName",
      label: "Product Name",
      sort: true,
      minWidth: 220,
      render: (row) => row?.productName || "-",
    },
    {
      id: "colorName",
      label: "Color",
      sort: true,
      minWidth: 140,
      render: (row) => row?.colorName || "-",
    },
    {
      id: "skuCount",
      label: "Total Sizes",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "perUnitCost",
      label: "Avg Cost Per Unit",
      sort: true,
      render: (row) => <span>{Number(row?.perUnitCost || 0).toFixed(2)}</span>,
    },
    {
      id: "totalOrdersCount",
      label: "Total Orders",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "totalDeliveredOrdersCount",
      label: "Total Delivered",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "totalReturnOrdersCount",
      label: "Total Returns",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "returnPercentage",
      label: "Return %",
      sort: true,
      render: (row) => <span>{Number(row?.returnPercentage || 0).toFixed(1)}%</span>,
    },
    {
      id: "totalCost",
      label: "Total Cost (A)",
      sort: true,
      isTotal: true,
      format: (v) => Number(v || 0).toFixed(2),
    },
    {
      id: "totalReceivedPayment",
      label: "Received Payment (B)",
      sort: true,
      isTotal: true,
      format: (v) => Number(v || 0).toFixed(2),
    },
    {
      id: "totalReturnPayment",
      label: "Return Charges (C)",
      sort: true,
      render: (row) => Math.abs(Number(row.totalReturnPayment || 0)).toFixed(2),
      isTotal: true,
      format: (v) => Number(v || 0).toFixed(2),
    },
    {
      id: "netPL",
      label: "Gross P/L (B-A-C)",
      sort: true,
      minWidth: 130,
      render: (row) => {
        const value = Number(row?.netPL || 0);
        return (
          <span style={{ color: value >= 0 ? "green" : "red", fontWeight: "bold" }}>
            {value >= 0 ? "+" : "-"}
            {Math.abs(value).toFixed(2)}
          </span>
        );
      },
      isTotal: true,
      format: (v) => Number(v || 0).toFixed(2),
    },
  ];

  const columns = groupBy === "color" ? colorColumns : sizeColumns;

  const sortData = (newOrder, newOrderBy) => {
    setOrder(newOrder);
    setOrderBy(newOrderBy);
  };

  return (
    <Box>
      {/* ── Header with group-by toggle ── */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Typography variant="h4" component="h1" sx={{ mb: 0 }}>
          Meesho Product Report
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant={groupBy === "size" ? "contained" : "outlined"}
            onClick={() => switchGroupBy("size")}
          >
            Size Wise
          </Button>
          <Button
            variant={groupBy === "color" ? "contained" : "outlined"}
            onClick={() => switchGroupBy("color")}
          >
            Color Wise
          </Button>
        </Stack>
      </Box>

      {/* ── Filters ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by product, SKU, color..."
                value={tempSearchTerm}
                onChange={(e) => setTempSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2.75}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date - To Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={dateRange}
                  onChange={setDateRange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={3.25}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ButtonGroup size="small" variant="outlined">
                  {QUICK_RANGES.map((r) => (
                    <Button
                      key={r.key}
                      onClick={() => setDateRange(r.getRange())}
                      disabled={meeshoData?.productReport?.loading}
                    >
                      {r.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={1.5}>
              <Button
                variant="contained"
                onClick={handleApplyFilter}
                disabled={meeshoData?.productReport?.loading}
                startIcon={
                  meeshoData?.productReport?.loading ? (
                    <CircularProgress size={16} />
                  ) : null
                }
                fullWidth
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Button
                variant="outlined"
                onClick={handleClear}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Table ── */}
      {meeshoData?.productReport?.loading ? (
        <Loader />
      ) : (
        <Card>
          <CardContent>
            <CustomTable
              key={`${groupBy}-${tableResetKey}`}
              columns={columns}
              rows={orders}
              page={page}
              rowsPerPage={rowsPerPage}
              count={meeshoData?.productReport?.total || 0}
              Order={order}
              OrderBy={orderBy}
              sortData={sortData}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              loading={meeshoData?.productReport?.loading}
              showTotalInFooter={true}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ProductReportList;
